import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Clock } from "lucide-react";
import TipThanks from "@/components/TipThanks";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useT } from "@/i18n/LanguageContext";

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 15;

export default function PaymentSuccess() {
  const { t } = useT();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  // PayPal redirects back with `?token=<order_id>&PayerID=...&provider=paypal`.
  const paypalOrderId = params.get("token");
  const provider = params.get("provider") || (paypalOrderId ? "paypal" : "stripe");
  const [state, setState] = useState("polling");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (provider === "paypal") {
      if (!paypalOrderId) { setState("failed"); return; }
      // PayPal is capture-on-return (not poll). One shot.
      let cancelled = false;
      (async () => {
        try {
          const { data: s } = await api.post("/paypal/capture-order", { order_id: paypalOrderId });
          if (cancelled) return;
          setData(s);
          setState(s.payment_status === "paid" ? "paid" : "failed");
        } catch { if (!cancelled) setState("failed"); }
      })();
      return () => { cancelled = true; };
    }

    // Stripe: poll until the webhook/status check marks it paid.
    if (!sessionId) { setState("failed"); return; }
    let polls = 0;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data: s } = await api.get(`/payments/status/${sessionId}`);
        setData(s);
        if (s.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired", "refunded"].includes(s.payment_status)) {
          setState("failed"); return;
        }
      } catch (e) {}
      polls += 1;
      if (polls >= MAX_POLLS) { setState("timeout"); return; }
      setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId, paypalOrderId, provider]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-amber-50 via-rose-50 to-amber-100 dark:from-stone-950 dark:via-stone-900 dark:to-stone-950">
      <div className="max-w-md w-full bg-background rounded-3xl border border-border shadow-xl p-8 text-center" data-testid="payment-success-page">
        {state === "polling" && (
          <>
            <Loader2 className="h-14 w-14 mx-auto text-amber-600 animate-spin mb-4" />
            <h1 className="font-display font-bold text-2xl mb-2">{t("payment.confirming")}</h1>
            <p className="text-sm text-stone-500 dark:text-stone-400">{t("payment.finalising")}</p>
          </>
        )}
        {state === "paid" && data && (
          <TipThanks
            amountCents={data.amount}
            message={data.tip_message}
            onDone={() => navigate("/")}
            doneLabel={t("payment.backToNotes")}
          />
        )}
        {state === "failed" && (
          <>
            <h1 className="font-display font-bold text-2xl mb-2">{t("payment.wentWrong")}</h1>
            <p className="text-sm text-stone-500 dark:text-stone-400 mb-4">{t("payment.couldNotConfirm")}</p>
            <Link to="/"><Button variant="outline" className="rounded-full">{t("payment.backToNotes")}</Button></Link>
          </>
        )}
        {state === "timeout" && (
          <>
            <Clock className="h-14 w-14 mx-auto text-amber-600 mb-4" />
            <h1 className="font-display font-bold text-2xl mb-2">{t("payment.stillProcessing")}</h1>
            <p className="text-sm text-stone-500 dark:text-stone-400 mb-4">{t("payment.stillProcessingHelp")}</p>
            <Link to="/"><Button variant="outline" className="rounded-full">{t("payment.backToNotes")}</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}
